import { useAppConfigStore } from '@shared/store/app-config'
import { storeToRefs } from 'pinia'
import { inject, ref, provide, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'

const INJECTION_KEY = 'ThBreadcrumbs'

export function useBreadcrumbsProvider() {
  // Some of the pages ( such as Accounting ) render ThPageWrappers as children of other
  // PageWrappers. Since we only want to have a single breadcrumb state for an entire parent-child chain,
  // we will check if there's an existing context provided by any of the parents, and if there is, we will
  // return it directly.
  const parentContext = useBreadcrumbs(null)

  if (parentContext) {
    return parentContext
  }

  const { navigation } = storeToRefs(useAppConfigStore())
  const route = useRoute() || { path: '/' }
  const { t } = useI18n()

  // The static trail is generated from the navigation definition, and goes down to listing views
  // Since the new/edit views are not part of the navigation structure, the breadcrumbs for those pages
  // will be generated dynamically
  const staticTrail = computed(() => {
    const trail = getStaticTrail(navigation.value, route.path).map(
      ({ key, fullRoute }) => {
        return {
          label: t(key),
          route: route.path === fullRoute ? null : fullRoute
        }
      }
    )

    // Home should be displayed on all routes
    if (route.path !== '/home') {
      trail.unshift({ label: t('nav.main.items.home.title'), route: '/home' })
    }

    return trail
  })

  // The dynamic trail is generated by rendering the <ThBreadcrumbItem /> component, which is usually
  // done inside forms, as the label of the breadcrumb is the resource name that is being viewed
  const dynamicTrail = ref([])

  const trail = computed(() => [...staticTrail.value, ...dynamicTrail.value])

  function getStaticTrail(navigation, path, trail = []) {
    const [parentPath, childPath] = path.split('/').filter(Boolean)

    const parentItem = navigation.find(
      ({ route }) => route === `/${parentPath}`
    )

    const childItem = parentItem?.children
      ? parentItem.children.find(({ route }) => route === `/${childPath}`)
      : null

    if (parentItem) {
      trail.push(parentItem)
    }

    if (childItem) {
      trail.push(childItem)
    }

    return trail
  }

  function addBreadcrumb(breadcrumb) {
    dynamicTrail.value.push(breadcrumb)
  }

  function removeBreadcrumb(breadcrumbToRemove) {
    dynamicTrail.value = dynamicTrail.value.filter(
      (breadcrumb) => breadcrumb !== breadcrumbToRemove
    )
  }

  const context = {
    trail,
    addBreadcrumb,
    removeBreadcrumb
  }

  provide(INJECTION_KEY, context)

  return context
}

export function useBreadcrumbs(defaultValue) {
  return inject(INJECTION_KEY, defaultValue)
}
